import { Injectable } from '@angular/core';
import { ApiService } from '../../../shared/services/core/api.service';
import { InputType, InputField, ProviderName } from '../../../account/models/login-response.model';
import { AuthService } from '../../../user/services/auth.service';
import { config } from '../../../shared/config';
import { map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NBAAuthService {
  /**
   * This holds all the nba settings required for sso,
   * this information will be read from the config js file
   * and also passed or defaulted from the login process.
   */

  constructor(
    private authService: AuthService,
    private api: ApiService
  ) {}

  /**
   * Attempt to login using nba using redirect process.
   * This will call an endpoint to generate the login URL,
   * depending on the parameters passed.
   * This can also be used for registration, handled by the parameters passed.
   * @param successredirectPath redirect path after successfully authenticated
   * e.g. /nba-callback (which is the default value)
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   */
  login(successredirectPath: string = '/nba-callback', stateValue: string = 'test', clubId: number = 1) {
    let fullSuccessPath = config.portalUrl + successredirectPath;
    const loginUrlParameters: InputField[] = this.generateLoginParameters(fullSuccessPath, stateValue);

    return this.api.post(`sso/OAuth/GenerateUrl/${ProviderName.NBA}/${clubId}`, loginUrlParameters).pipe(
      tap((generatedUrl: any) => {
        window.location.href = generatedUrl;
      })
    );
  }

  /**
   * This method will generate the login
   * URL for NBA from the defined
   * settings, when login has been attempted.
   *
   * @returns array of parameters required
   * to generate the NBA
   * login URL of type InputField.
   */
  generateLoginParameters(fullSuccessPath: string, stateValue: string) {
    return [
      {
        FieldName: 'RedirectUrl',
        InputType: InputType.Text,
        Active: true,
        Value: fullSuccessPath,
        ErrorMessage: '',
      },
      {
        FieldName: 'State',
        InputType: InputType.Text,
        Active: true,
        Value: stateValue,
        ErrorMessage: '',
      },
    ];
  }

  /**
   * This method is used to authenticate a token
   * that comes back from the NBA login page
   * and log the user in via the tokens returned
   * from the response.
   * @param code code returned from
   * NBA, to be authenticated and
   * passed to given endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  validateCode(code: string, clubId: number = 1) {
    if (this.authService.hasAccessToken()) {
      return of(true);
    }

    return this.api.post(`sso/OAuth/${ProviderName.NBA}/${code}/${clubId}`, `"${code}"`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          localStorage.setItem('NBARefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('NBAAccessToken', authResponse.auth.AccessToken.toString());
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
        }
      }),
      map((authResponse: any) => {
        if (authResponse) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * This method is used to authenticate a domain
   * token used from the _nba_sso_upid cookie given
   * from Key NBA domain and log the user
   * using tokens returned.
   * @param domainCookie domain cookie to be
   * authenticated and passed to endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  loginUsingToken(domainCookie: string, clubId: number = 1) {
    let nbaDomainCookie = '';
    if (domainCookie != '' && domainCookie != undefined) {
      nbaDomainCookie = domainCookie;
    }

    return this.api.get(`sso/OAuth/${ProviderName.NBA}/${nbaDomainCookie}/${clubId}`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('NBARefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('NBAAccessToken', authResponse.auth.AccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }
}
